import {
  CollectDataStepType,
  CollectDataFormItemType,
  MedicalCardRequirement,
} from 'src/constants/collect-data/enums'

// ─── Approval Types ──────────────────────────────────────────────────────────

export enum StepApprovalType {
  AutoApproval = 'auto_approval',
  CustomerApproval = 'customer_approval',
  InternalApproval = 'internal_approval',
  InternalThenCustomerApproval = 'internal_then_customer_approval',
  CustomerThenInternalApproval = 'customer_then_internal_approval',
}

// ─── Form Item Types (admin/template level) ──────────────────────────────────

export interface FormItemOption {
  id: string
  title: string
  isCorrect?: boolean
}

interface BaseFormItem {
  id: string
  type: CollectDataFormItemType
}

export interface TextBlockFormItem extends BaseFormItem {
  type: CollectDataFormItemType.TextBlock
  content: string
}

export interface VideoFormItem extends BaseFormItem {
  type: CollectDataFormItemType.Video
  title: string
  url: string
}

export interface TextInputFormItem extends BaseFormItem {
  type: CollectDataFormItemType.TextInput
  title: string
  required: boolean
}

export interface DateFormItem extends BaseFormItem {
  type: CollectDataFormItemType.Date
  title: string
  required: boolean
}

export interface NumberFormItem extends BaseFormItem {
  type: CollectDataFormItemType.Number
  title: string
  required: boolean
}

export interface SelectFormItem extends BaseFormItem {
  type: CollectDataFormItemType.Select
  title: string
  options: FormItemOption[]
  required: boolean
  checkAnswers: boolean
}

export interface RadioFormItem extends BaseFormItem {
  type: CollectDataFormItemType.Radio
  title: string
  options: FormItemOption[]
  required: boolean
  checkAnswers: boolean
}

export interface CheckboxFormItem extends BaseFormItem {
  type: CollectDataFormItemType.Checkbox
  title: string
  options: FormItemOption[]
  required: boolean
  checkAnswers: boolean
}

export interface FileFormItem extends BaseFormItem {
  type: CollectDataFormItemType.File
  title: string
  required: boolean
}

export type TemplateFormItem =
  | TextBlockFormItem
  | VideoFormItem
  | TextInputFormItem
  | DateFormItem
  | NumberFormItem
  | SelectFormItem
  | RadioFormItem
  | CheckboxFormItem
  | FileFormItem

// ─── API form items (`CollectDataFormTemplateItem` on Nest) ─────────────────

/** `CollectDataFormTemplateAnswer` / `CollectDataFormTemplateAnswerDto` */
export type CollectDataFormTemplateAnswer = FormItemOption

interface BasicCollectDataFormTemplateItem {
  id: string
  type: CollectDataFormItemType
}

export interface CollectDataFormTemplateTextBlock extends BasicCollectDataFormTemplateItem {
  type: CollectDataFormItemType.TextBlock
  text: string
}

export interface CollectDataFormTemplateTextInput extends BasicCollectDataFormTemplateItem {
  type: CollectDataFormItemType.TextInput
  title: string
  required: boolean
}

export interface CollectDataFormTemplateDate extends BasicCollectDataFormTemplateItem {
  type: CollectDataFormItemType.Date
  title: string
  required: boolean
}

export interface CollectDataFormTemplateNumber extends BasicCollectDataFormTemplateItem {
  type: CollectDataFormItemType.Number
  title: string
  required: boolean
}

export interface CollectDataFormTemplateSelect extends BasicCollectDataFormTemplateItem {
  type: CollectDataFormItemType.Select
  title: string
  options: CollectDataFormTemplateAnswer[]
  checkAnswers: boolean
  required: boolean
}

export interface CollectDataFormTemplateRadio extends BasicCollectDataFormTemplateItem {
  type: CollectDataFormItemType.Radio
  title: string
  options: CollectDataFormTemplateAnswer[]
  checkAnswers: boolean
  required: boolean
}

export interface CollectDataFormTemplateCheckbox extends BasicCollectDataFormTemplateItem {
  type: CollectDataFormItemType.Checkbox
  title: string
  options: CollectDataFormTemplateAnswer[]
  checkAnswers: boolean
  required: boolean
}

export interface CollectDataFormTemplateVideo extends BasicCollectDataFormTemplateItem {
  type: CollectDataFormItemType.Video
  title: string
  url: string
}

export interface CollectDataFormTemplateFile extends BasicCollectDataFormTemplateItem {
  type: CollectDataFormItemType.File
  title: string
  required: boolean
}

export type CollectDataFormTemplateSubmittableItem =
  | CollectDataFormTemplateTextInput
  | CollectDataFormTemplateDate
  | CollectDataFormTemplateNumber
  | CollectDataFormTemplateSelect
  | CollectDataFormTemplateRadio
  | CollectDataFormTemplateCheckbox
  | CollectDataFormTemplateFile

export type CollectDataFormTemplateItem =
  | CollectDataFormTemplateSubmittableItem
  | CollectDataFormTemplateTextBlock
  | CollectDataFormTemplateVideo

// ─── Step Configs (per step type metadata) ───────────────────────────────────

export interface BaseStepConfig {
  approvalType: StepApprovalType
  delayed: boolean
}

export interface DriverLicenseStepConfig extends BaseStepConfig {}

export interface BusinessEntityStepConfig extends BaseStepConfig {
  /** From API `collectDataTemplateSteps[].optional` (step row, not `metadata`). */
  optional: boolean
  /** From API `collectDataTemplateSteps[].maxAttempts` (step row, not `metadata`). */
  maxAttempts: number
}

export interface BackgroundCheckStepConfig extends BaseStepConfig {
  /** From API `collectDataTemplateSteps[].optional` (step row, not `metadata`). */
  optional: boolean
  /** Read from API `metadata.provider`; sent back unchanged — the editor has no provider picker. */
  provider?: BackgroundCheckProvider
  /** Maps to API `metadata.packageId`. */
  yardstikPackage: string
  /** Maps to API `metadata.enableProvider`. */
  enableYardstik: boolean
  continueOnIncomplete: boolean
  manualUpload: boolean
  expiresInDays: number
}

/** MVR shares the same Yardstik / expiry fields as BGC in the API `metadata` payload */
export interface MvrUploadStepConfig extends BaseStepConfig {
  /** From API `collectDataTemplateSteps[].optional` (step row, not `metadata`). */
  optional: boolean
  /** Read from API `metadata.provider`; sent back unchanged — the editor has no provider picker. */
  provider?: BackgroundCheckProvider
  /** Maps to API `metadata.packageId`. */
  yardstikPackage: string
  /** Maps to API `metadata.enableProvider`. */
  enableYardstik: boolean
  continueOnIncomplete: boolean
  manualUpload: boolean
  expiresInDays: number
}

export interface SsnAndAddressStepConfig extends BaseStepConfig {
  /** From API `collectDataTemplateSteps[].optional` (step row, not `metadata`). */
  optional: boolean
  requireProofOfWorkAuthorization: boolean
  enforceSsnCollection: boolean
}

export interface VehicleAndInsuranceStepConfig extends BaseStepConfig {
  disableInsuranceUpload: boolean
  requireExpirationDate: boolean
}

export interface InsuranceStepConfig extends BaseStepConfig {
  /** Parent `collectDataTemplateStepsConfigs[].insurancePolicyId` from the API. */
  insurancePolicyId: string | null
  /** Config group display title (`collectDataTemplateStepsConfigs[].title`). */
  groupTitle: string
  /**
   * Product selection; persisted on the step row as `metadata.insurancePolicyProductId`.
   */
  insurancePolicyProductId: string | null
  /** `collectDataTemplateSteps[].label` (unique per step instance). */
  label: string
}

export interface FormStepConfig {
  label: string
  optional: boolean
  approvalType: StepApprovalType
  name: string
  description: string
  maxAttempts: number
  formItems: TemplateFormItem[]
}

export interface McNumberStepConfig extends BaseStepConfig {
  /** From API `collectDataTemplateSteps[].optional` (step row, not `metadata`). */
  optional: boolean
}
export interface DotNumberStepConfig extends BaseStepConfig {
  /** From API `collectDataTemplateSteps[].optional` (step row, not `metadata`). */
  optional: boolean
  /** Maps to API `metadata.medicalCardRequirement`. */
  medicalCardRequirement: MedicalCardRequirement
}
export interface LmsTrainingStepConfig extends BaseStepConfig {
  lmsTrainingId: string
  lmsTrainingName: string
  /** Mirrors API `expiresInDays` on the step row (not in `metadata`) */
  expiresInDays: number
  /** Maps to `metadata.enableManualUpload` on the collect-data template step. */
  manualUpload: boolean
}
export interface SignContractStepConfig extends BaseStepConfig {
  docusignTemplateId: string
}
export interface InsuranceAmountDeclarationStepConfig extends BaseStepConfig {}
export interface InsuranceBeneficiaryStepConfig extends BaseStepConfig {}
export interface VehicleRegistrationStepConfig extends BaseStepConfig {
  requireExpirationDate: boolean
}
export interface PaymentMethodStepConfig extends BaseStepConfig {}

export type StepConfig =
  | DriverLicenseStepConfig
  | BusinessEntityStepConfig
  | BackgroundCheckStepConfig
  | SsnAndAddressStepConfig
  | VehicleAndInsuranceStepConfig
  | InsuranceStepConfig
  | FormStepConfig
  | MvrUploadStepConfig
  | McNumberStepConfig
  | DotNumberStepConfig
  | LmsTrainingStepConfig
  | SignContractStepConfig
  | InsuranceAmountDeclarationStepConfig
  | InsuranceBeneficiaryStepConfig
  | VehicleRegistrationStepConfig
  | PaymentMethodStepConfig

// ─── Template Step ───────────────────────────────────────────────────────────

/** Scalar fields on `collectDataTemplateSteps[]` rows (not in step `metadata` / `config`). */
export interface OnboardingTemplateStepRowScalars {
  retroactiveCollectRequired: boolean
  ignoreMonitoring: boolean
  recollectSameTypeSteps: boolean
  skipRetroactiveCollectIfAnySameTypeCompleted: boolean
}

export function defaultOnboardingTemplateStepRowScalars(): OnboardingTemplateStepRowScalars {
  return {
    retroactiveCollectRequired: false,
    ignoreMonitoring: false,
    recollectSameTypeSteps: false,
    skipRetroactiveCollectIfAnySameTypeCompleted: false,
  }
}

export interface OnboardingTemplateStep extends OnboardingTemplateStepRowScalars {
  id: string
  type: CollectDataStepType
  order: number
  config: StepConfig
  /** Present when this step came from (or belongs to) a `collectDataTemplateStepsConfigs` group */
  collectDataTemplateStepsConfigId?: string
  /**
   * Unique `collectDataTemplateSteps[].label` from the API (e.g. `sign_contract1` vs `sign_contract2`).
   * Must be preserved on save; new client steps use `type` until persisted.
   */
  collectDataTemplateStepLabel: string
}

// ─── API: collect-data template (GET / collect-data-templates) ───────────────
// Mirrors Nest: CollectDataTemplateDto, CollectDataTemplateStepsConfigDto,
// CollectDataTemplateStepDto, and CollectDataTemplateStep*Metadata types.

/** Same values as backend `CollectDataApprovalType`. */
export type CollectDataApprovalType = StepApprovalType

export interface CollectDataTemplateStepSignContractMetadata {
  docusignTemplateId: string
}

export interface CollectDataTemplateStepFormMetadata {
  title: string
  description: string
  formItems: CollectDataFormTemplateItem[]
}

/** Background-check vendor a `background_check` / `mvr_upload` step runs against. */
export type BackgroundCheckProvider = 'yardstik' | 'checkr'

export interface CollectDataTemplateStepBackgroundCheckMetadata {
  /** Always present on read; optional on write — omit to leave the stored provider as-is. */
  provider?: BackgroundCheckProvider
  packageId?: string
  enableProvider?: boolean
  enableManualUpload?: boolean
}

export interface CollectDataTemplateStepMvrMetadata {
  /** Always present on read; optional on write — omit to leave the stored provider as-is. */
  provider?: BackgroundCheckProvider
  packageId?: string
  enableProvider?: boolean
  enableManualUpload?: boolean
}

/** Nested product on insurance step rows when API embeds full product (read-only in editor). */
export interface InsurancePolicyProductFull {
  id: string
  allowOptOut?: boolean
  feeCents?: number
  feePercent?: string
  feeCapCents?: number
  insuranceProductId?: string
  insurancePolicyId?: string
  insuranceDocusignTemplateId?: string | null
  description?: string
  explanation?: string | null
  requireDocumentUploadWhenOptedIn?: boolean
  documentUploadTitleWhenOptedIn?: string
  createdAt?: number
  updatedAt?: number
  insuranceProduct?: {
    name: string
    description?: string
    explanation?: string
  }
}

export interface CollectDataTemplateStepInsuranceMetadata {
  insurancePolicyProductId: string
  insurancePolicyProduct?: InsurancePolicyProductFull
}

export interface CollectDataTemplateStepLmsTrainingMetadata {
  lmsTrainingId: string
  lmsTrainingName: string
  partnerDriverLmsUserEmail?: string
  enableManualUpload?: boolean
}

export interface CollectDataTemplateStepVehicleAndInsuranceMetadata {
  disableInsuranceUpload?: boolean
}

export interface CollectDataTemplateStepSsnAndAddressMetadata {
  requireWorkAuthorizationProof?: boolean
  /** Backend field name (`CollectDataTemplateStepSsnAndAddressMetadata`). */
  enforceSsnCollection?: boolean
}

export interface CollectDataTemplateStepDotNumberMetadata {
  medicalCardRequirement?: MedicalCardRequirement
}

export interface PaymentMethodDetails {
  paymentMethodType: string
  nameOnAccount: string
  last4: string
  accountType?: string
  cardType?: string
  expiryDate?: string
}

export interface CollectDataTemplateStepPaymentMethodMetadata {
  paymentMethodDetails?: PaymentMethodDetails
}

export type CollectDataTemplateStepMetadata =
  | CollectDataTemplateStepSignContractMetadata
  | CollectDataTemplateStepFormMetadata
  | CollectDataTemplateStepBackgroundCheckMetadata
  | CollectDataTemplateStepMvrMetadata
  | CollectDataTemplateStepInsuranceMetadata
  | CollectDataTemplateStepLmsTrainingMetadata
  | CollectDataTemplateStepVehicleAndInsuranceMetadata
  | CollectDataTemplateStepSsnAndAddressMetadata
  | CollectDataTemplateStepPaymentMethodMetadata
  | CollectDataTemplateStepDotNumberMetadata

/** Flat metadata keys for diff/patch helpers (union members share field names). */
export type CollectDataTemplateStepMetadataFields = Partial<
  CollectDataTemplateStepSignContractMetadata &
    CollectDataTemplateStepFormMetadata &
    CollectDataTemplateStepBackgroundCheckMetadata &
    CollectDataTemplateStepMvrMetadata &
    CollectDataTemplateStepInsuranceMetadata &
    CollectDataTemplateStepLmsTrainingMetadata &
    CollectDataTemplateStepVehicleAndInsuranceMetadata &
    CollectDataTemplateStepSsnAndAddressMetadata &
    CollectDataTemplateStepPaymentMethodMetadata &
    CollectDataTemplateStepDotNumberMetadata
>

/** `CollectDataTemplateStepDto` */
export interface CollectDataTemplateStepRow {
  id: string
  type: CollectDataStepType
  label?: string
  metadata?: CollectDataTemplateStepMetadataFields
  order: number
  optional: boolean
  retroactiveCollectRequired: boolean
  expiresInDays?: number
  requireExpirationDate?: boolean
  maxAttempts: number
  approvalType: CollectDataApprovalType
  ignoreMonitoring: boolean
  continueOnIncomplete?: boolean
  recollectSameTypeSteps: boolean
  skipRetroactiveCollectIfAnySameTypeCompleted: boolean
  collectDataTemplateId: string
  collectDataTemplateStepsConfigId: string
}

/** `CollectDataTemplateStepsConfigDto` + nested steps (`CollectDataTemplateStepsConfigResponseDto`). */
export interface CollectDataTemplateStepsConfig {
  id: string
  label: string
  delayed: boolean
  title: string | null
  /** Present on create/update config DTOs; may be absent on GET response. */
  description?: string
  order: number
  collectDataTemplateId: string
  insurancePolicyId?: string
  collectDataTemplateSteps: CollectDataTemplateStepRow[]
}

/** `CollectDataTemplateDto` + configs (`AdminCreateCollectDataTemplateResponseDto`). */
export interface OnboardingTemplate {
  id: string
  name: string
  partnerId: string
  collectDataTemplateStepsConfigs: CollectDataTemplateStepsConfig[]
  createdAt?: number
  updatedAt?: number
}
